import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Printer } from "lucide-react";
import { ACTS } from "@/features/demo/tour";

type Slide = { kicker: string; title: string; points: string[] };

const SLIDES: Slide[] = [
  {
    kicker: "WeKIT Career Clarity 360™",
    title: "People are potential waiting to be unleashed.",
    points: ["A career discovery experience that shows what's possible today — never a fixed destiny."],
  },
  {
    kicker: "The problem",
    title: "Most career tests measure who is willing to tick the right box.",
    points: [
      "Long questionnaires with obvious right answers.",
      "Generic labels with no reason why.",
      "No path from result to real action.",
    ],
  },
  ...ACTS.map((a) => ({ kicker: `Pillar ${a.n}`, title: a.title, points: a.say })),
  {
    kicker: "Next step",
    title: "Let's unlock potential together.",
    points: ["See it live at wekit.ai", "support@wekit.ai"],
  },
];

export default function PitchDeck() {
  const [i, setI] = useState(() => Number(new URLSearchParams(location.search).get("slide") ?? 1) - 1 || 0);
  const s = SLIDES[i];

  useEffect(() => {
    history.replaceState(null, "", `?slide=${i + 1}`);
    document.title = `${i + 1}/${SLIDES.length} — ${s.title}`;
  }, [i, s.title]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (["ArrowRight", " "].includes(e.key)) setI((v) => Math.min(SLIDES.length - 1, v + 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  const slideEl = (sl: Slide, n: number) => (
    <section key={n} className="deck-slide flex aspect-video w-full flex-col justify-center rounded-3xl border border-border bg-card p-8 sm:p-16">
      <p className="text-sm font-medium uppercase tracking-widest text-primary">{sl.kicker}</p>
      <h2 className="mt-4 max-w-4xl font-heading text-3xl font-bold leading-tight sm:text-6xl">{sl.title}</h2>
      <ul className="mt-8 space-y-3 text-lg text-muted-foreground sm:text-2xl">
        {sl.points.map((p) => <li key={p}>{p}</li>)}
      </ul>
      <p className="mt-auto pt-6 text-sm text-muted-foreground">WeKIT · {n + 1} / {SLIDES.length}</p>
    </section>
  );

  return (
    <main className="mx-auto max-w-6xl px-4 py-8">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/demo" className="text-sm text-muted-foreground hover:underline">← Presenter mode</Link>
        <button onClick={() => window.print()} className="inline-flex min-h-12 items-center gap-2 rounded-full bg-primary px-6 font-semibold text-primary-foreground">
          <Printer className="h-5 w-5" aria-hidden /> Save as PDF
        </button>
      </div>

      <div
        className="no-print"
        onTouchStart={(e) => ((e.currentTarget as HTMLElement).dataset.x = String(e.touches[0].clientX))}
        onTouchEnd={(e) => {
          const dx = e.changedTouches[0].clientX - Number((e.currentTarget as HTMLElement).dataset.x);
          if (dx < -50) setI((v) => Math.min(SLIDES.length - 1, v + 1));
          if (dx > 50) setI((v) => Math.max(0, v - 1));
        }}
      >
        {slideEl(s, i)}
        <div className="mt-4 flex items-center justify-center gap-4">
          <button aria-label="Previous slide" onClick={() => setI(Math.max(0, i - 1))} className="grid h-12 w-12 place-items-center rounded-full border border-border hover:bg-muted">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="tabular-nums text-muted-foreground">{i + 1} / {SLIDES.length}</span>
          <button aria-label="Next slide" onClick={() => setI(Math.min(SLIDES.length - 1, i + 1))} className="grid h-12 w-12 place-items-center rounded-full border border-border hover:bg-muted">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="print-only hidden print:block">{SLIDES.map(slideEl)}</div>
      <style>{`@media print { @page { size: A4 landscape; margin: 10mm } .no-print { display: none !important; } .deck-slide { break-after: page; border: none; } }`}</style>
    </main>
  );
}
